
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { publicFeatures } from '../../config/publicFeatures';

const ALL_FAQS = [
  {
    question: "Is this product genuine and authentic?",
    answer: "Yes, all products tracked on TechieDay are sourced from Amazon's official marketplace with verified sellers. We only track authentic products with proper warranties."
  },
  {
    question: "How accurate are the price tracking updates?",
    answer: "Our price tracking system updates multiple times daily. Prices are synced directly from Amazon's API to ensure accuracy. However, prices can change rapidly, so we recommend checking the current price on Amazon before purchasing."
  },
  {
    question: "Can I get notified when the price drops?",
    answer: "Absolutely! Click the 'Track Price' or bell icon to set up price alerts. You'll receive notifications via email when the price drops below your target threshold."
  },
  {
    question: "What does the price history show?",
    answer: "Our price history charts show how the product's price has changed over time, including lowest and highest prices, average price trends, and the best times to buy."
  },
  {
    question: "Are there any additional fees when buying through our links?",
    answer: "No additional fees for you! We earn a small commission from Amazon when you make a purchase through our links, but this doesn't affect the price you pay."
  }
];

// FAQ entries about hidden features are removed from the default list while those features are off.
const HIDDEN_FAQ_QUESTIONS = [
  ...(publicFeatures.priceHistory ? [] : ['How accurate are the price tracking updates?', 'What does the price history show?']),
  ...(publicFeatures.priceAlerts ? [] : ['Can I get notified when the price drops?']),
];
const defaultFAQs = ALL_FAQS.filter((faq) => !HIDDEN_FAQ_QUESTIONS.includes(faq.question));

export function ProductFAQ({ product, faqs = defaultFAQs }) {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-6">
        <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Frequently Asked Questions
        </h3>
      </div>

      <div className="space-y-3">
        {faqs.map((faq, index) => (
          <div 
            key={index}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full px-4 py-4 text-left bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors flex items-center justify-between gap-4"
              data-analytics-id={`faq-toggle-${index}`}
            >
              <span className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                {faq.question}
              </span>
              <ChevronDown 
                size={20}
                className={`text-gray-500 dark:text-gray-400 transition-transform flex-shrink-0 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
              />
            </button>
            
            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 py-4 bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}
